import type { OrderData } from '../types/chartData'

/**
 * Pairing a run's `opened` events with their `closed` ones.
 *
 * Not by `orderId`: the datafeed's id is the *event* id, so an entry and its matching exit
 * carry different ones. What the two share is the position: its engine `ticket` on rows
 * recorded by engine revision 4 or later, and before that only its entry time (`order.time`
 * on the `opened` event, `order.entryTime` on the `closed` one).
 *
 * `pairKeyOf` is that identity: `t:<ticket>` when the row carries one, else `e:<entry time>`,
 * which is exactly the old entry-time pairing, same-second collision included. Every reader in
 * the walkthrough (the trades pane, the chart's working lines, the order tooltip, the period
 * sizing) keys on it, so none of them can disagree about whether a position is still open.
 */
export type PairKey = string

/** The identity of the position an event belongs to, or null for an unpairable close. */
export function pairKeyOf(o: OrderData): PairKey | null {
  if (o.ticket != null) return `t:${o.ticket}`
  const entry = o.eventType === 'closed' ? (o.entryTime ?? null) : o.time
  return entry == null ? null : `e:${entry}`
}

/**
 * Position key -> the closing event, for every close the reveal cursor has reached.
 *
 * `upToTs` is the reveal cursor. A close beyond it has not happened yet as far as the replay
 * is concerned, so it is left out — which is what makes "is this position still open?"
 * answerable from the map alone.
 */
export function closesByKey(orders: OrderData[], upToTs = Infinity): Map<PairKey, OrderData> {
  const legs = closeLegsByKey(orders, upToTs)
  const opened = new Map<PairKey, number | undefined>()
  for (const o of orders) {
    if (o.eventType !== 'opened') continue
    const key = pairKeyOf(o)
    if (key != null) opened.set(key, o.volume)
  }
  const byKey = new Map<PairKey, OrderData>()
  for (const [key, closes] of legs) {
    // Ticketed rows close once. Legacy rows close in parts: closed once the whole volume is.
    if (!key.startsWith('t:')) {
      const volume = opened.get(key)
      const closedVolume = closes.reduce((sum, c) => sum + (c.volume ?? 0), 0)
      const partsKnown = volume != null && volume > 0 && closes.every((c) => c.volume != null)
      if (partsKnown && closedVolume < volume - VOLUME_EPSILON) continue
    }
    byKey.set(key, closes[closes.length - 1])
  }
  return byKey
}

const VOLUME_EPSILON = 1e-6

/** Every close a position has had by `upToTs`, oldest first. */
export function closeLegsByKey(orders: OrderData[], upToTs = Infinity): Map<PairKey, OrderData[]> {
  const byKey = new Map<PairKey, OrderData[]>()
  for (const o of orders) {
    if (o.eventType !== 'closed') continue
    if (o.time > upToTs) continue
    const key = pairKeyOf(o)
    if (key == null) continue
    const legs = byKey.get(key)
    if (legs) legs.push(o)
    else byKey.set(key, [o])
  }
  for (const legs of byKey.values()) legs.sort((a, b) => a.time - b.time)
  return byKey
}

/** A partial close's share of its position, in whole percent; null for a single close. */
export function closeShare(close: OrderData, orders: OrderData[]): number | null {
  if (close.eventType !== 'closed') return null
  const key = pairKeyOf(close)
  if (key == null) return null
  const legs = closeLegsByKey(orders).get(key) ?? []
  if (legs.length < 2) return null
  const opened = orders.find((o) => o.eventType === 'opened' && pairKeyOf(o) === key)
  if (!opened?.volume || close.volume == null) return null
  return Math.round((close.volume / opened.volume) * 100)
}

/** The `opened` event's id -> its close, for closes at or before `upToTs`. */
export function pairOpensToCloses(
  orders: OrderData[],
  upToTs = Infinity,
): Map<OrderData['orderId'], OrderData> {
  const closes = closesByKey(orders, upToTs)
  const paired = new Map<OrderData['orderId'], OrderData>()
  for (const o of orders) {
    if (o.eventType !== 'opened') continue
    const key = pairKeyOf(o)
    const close = key == null ? undefined : closes.get(key)
    if (close) paired.set(o.orderId, close)
  }
  return paired
}

/**
 * Display numbers for a run: position key -> 1-based number, in the order the positions were
 * entered.
 *
 * This is the "#1 / #2" the user reads — in the trades pane, and on the chart's order tooltip.
 * One function because those two MUST agree. Numbers cover every entry in the set given,
 * including ones the playhead has not reached yet, so a trade keeps its number for the whole
 * replay rather than being renumbered as it goes. Legacy rows without tickets keep the old
 * behaviour: two positions opened in the same second share a number.
 */
export function ticketsByKey(orders: OrderData[]): Map<PairKey, number> {
  const entries = orders
    .filter((o) => o.eventType === 'opened')
    .map((o) => ({ key: pairKeyOf(o), time: o.time, ticket: o.ticket ?? Number.MAX_SAFE_INTEGER }))
    .filter((e): e is { key: PairKey; time: number; ticket: number } => e.key != null)
    .sort((a, b) => a.time - b.time || a.ticket - b.ticket)

  const tickets = new Map<PairKey, number>()
  for (const e of entries) {
    if (!tickets.has(e.key)) tickets.set(e.key, tickets.size + 1)
  }
  return tickets
}

/** Signed pip movement, in the direction the position was taken. */
export function tradePips(
  type: 'BUY' | 'SELL',
  entry: number,
  price: number,
  pipSize: number,
): number | null {
  if (!Number.isFinite(pipSize) || pipSize <= 0) return null
  const raw = type === 'SELL' ? entry - price : price - entry
  return raw / pipSize
}
